"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.sendPostMessage = exports.constructPostMessage = exports.getPostMessageTargetOrigin = exports.getTargetWindow = exports.listenForPostMessage = exports.MSG_KEYS = void 0;
const global_utils_1 = require("../utils/global-utils");
const launch_utils_1 = require("../utils/launch-utils");
// ----------------------------------------
// Constants
const POST_MSG_KEY_PREFIX = "chat-post-message--";
exports.MSG_KEYS = {
  // For facilitating cross-window `localStorage` parallel cache
  CROSS_WINDOW_LOCALSTORAGE: `${POST_MSG_KEY_PREFIX}cross-window-localstorage`,
  // For communicating client context changes (e.g. "pop-out")
  INFORM_CLIENT_CONTEXT_CHANGE: `${POST_MSG_KEY_PREFIX}inform-client-context-change`,
  // Flag for chat launcher to send through to chat client in
  // order to manually terminate chat (via <Toolbar/> close button)
  CLIENT_END_CHAT: `${POST_MSG_KEY_PREFIX}client-end-chat`,
  // When chat status changes
  CHAT_STATUS_CHANGE: `${POST_MSG_KEY_PREFIX}chat-status-change`,
  // Communicated by the client to applicable window openers,
  // when new messages received. To facilitate display of unread messages
  RECEIVED_MESSAGES_LENGTH: `${POST_MSG_KEY_PREFIX}received-messages-length`,
  // Confirmation that client has successfully loaded (bare minimum)
  // NOTE: This line specifically is not prefixed so as to be backwards-compatible
  // with the existing chat launcher code, which uses this exact string
  CLIENT_HAS_LOADED: "loaded",
  CLIENT_READY_TO_RENDER: `${POST_MSG_KEY_PREFIX}ready-to-render`,
  // This next line is ONLY used by the new chat launcher (this package),
  // since the legacy chat launcher didn't use the keyed `postMessage` system
  // that this package does, and just sends the payload without an identifier
  TRANSMIT_CONFIG: `${POST_MSG_KEY_PREFIX}load-config`,
  // Communicated by the launcher back to the client in the scenario where
  // a user navigates from page to page while using the inline chat
  PAGE_NAVIGATION_CHANGE: `${POST_MSG_KEY_PREFIX}most-recent-page`
};
// ----------------------------------------
// Functions
/**
 * Listen for window `postMessage` event, with message
 * value corresponding to the provided `msgKey` event.
 *
 * Note the `onSuccess`/`onError` callbacks are contingent
 * on the init origin being from a valid location.
 */
const listenForPostMessage = _ref => {
  let {
    msgKey,
    onlyOnce,
    onSuccess,
    onError
  } = _ref;
  window.addEventListener("message", function callback(event) {
    const msgKeyRegex = new RegExp(`^${msgKey}`);
    // Confirm message data indicates load confirmation
    if (typeof (event === null || event === void 0 ? void 0 : event.data) === "string" && !!(event === null || event === void 0 ? void 0 : event.data.match(msgKeyRegex))) {
      // Respect opt-in flag for once-only event fire
      if (onlyOnce) {
        window.removeEventListener("message", callback);
      }
      // Execute error or success callback based on
      // whether the event is from a permitted origin
      if ((0, launch_utils_1.verifyInitOrigin)(event.origin)) {
        const toParse = event.data.replace(msgKeyRegex, "");
        try {
          const payload = toParse !== "" ? JSON.parse(toParse) : undefined;
          // Include the parsed payload in the success callback
          (0, global_utils_1.execCallback)(onSuccess, event, payload !== null && payload !== void 0 ? payload : {});
        } catch (e) {
          console.error("Soft error attempting JSON parse:", e);
        }
      } else {
        (0, global_utils_1.execCallback)(onError, event);
      }
    }
  }, false);
};
exports.listenForPostMessage = listenForPostMessage;
/**
 * Retrieve either the opening window, or parent window.
 * (Depends on either pop-up or iframe origin, if applicable).
 *
 * Returns null if there is no originating window.
 */
const getTargetWindow = () => {
  const popupWindow = window.opener;
  const iframeWindow = window.parent;
  return popupWindow !== null && popupWindow !== void 0 ? popupWindow : iframeWindow;
};
exports.getTargetWindow = getTargetWindow;
/**
 * When using `window.postMessage`, only allow same-origin communication,
 * with an allowance for local development (where port numbers dont match).
 */
const getPostMessageTargetOrigin = () => {
  const isLocal = (0, global_utils_1.isLocalhost)(window.origin);
  const targetOrigin = `${window.location.protocol}//${window.location.hostname}`;
  return isLocal ? "*" : targetOrigin;
};
exports.getPostMessageTargetOrigin = getPostMessageTargetOrigin;
/**
 * Format a string with a message key prefix identifier,
 * and stringified payload attached thereafter. This format
 * is used/expected for both send/receive postMessage functions.
 */
const constructPostMessage = (msgKey, payload) => `${msgKey}${payload ? ` ${JSON.stringify(payload)}` : ""}`;
exports.constructPostMessage = constructPostMessage;
/**
 * Send `postMessage` to an optionally specified (or calculated)
 * target window, with an optional information payload.
 */
const sendPostMessage = _ref2 => {
  let {
    msgKey,
    targetWindow = (0, exports.getTargetWindow)(),
    payload
  } = _ref2;
  if (!targetWindow) return;
  targetWindow === null || targetWindow === void 0 ? void 0 : targetWindow.postMessage((0, exports.constructPostMessage)(msgKey, payload), (0, exports.getPostMessageTargetOrigin)());
};
exports.sendPostMessage = sendPostMessage;